<template>
    <slot name="trigger" :click="open"></slot>
    <modal v-model="modal" allow-close>
        <template v-slot:header>Create New Article</template>
        <div class="form-group">
            <label>Title</label>
            <input type="text" name="social-title" :class="{'is-invalid':!validation.title}" class="form-control" v-model="title" required maxlength="250">
            <small v-if="!validation.title" class="invalid-feedback">A title is required</small>
        </div>
        <div class="form-group">
            <label>Description</label>
            <textarea :class="{'is-invalid':!validation.description}" class="form-control" v-model="description" maxlength="400" required rows="3"></textarea>
            <small v-if="!validation.description" class="invalid-feedback">A description is required</small>
        </div>
        <div class="form-group">
            <label>Article Text</label>
            <textarea :class="{'is-invalid':!validation.article_text}" class="form-control" v-model="article_text" required rows="6"></textarea>
            <small v-if="!validation.article_text" class="invalid-feedback">The main body of the article is required</small>
        </div>
        <div class="row">
            <div class="form-group col-md-5">
                <label>Author</label>&nbsp; &nbsp;
                <select v-model="selected_author">
                    <option :value="null"> -- Select An Author --</option>
                    <option v-for="author in authorsObj" v-bind:key="author.author_id" :value="author.author_id">{{author.full_name}}</option>
                </select>
            </div>
            <div class="form-group col-md-7">
                <label>Publish On</label>
                <datepicker :class="{'is-invalid':!validation.published_on}" class="form-control" :lower-limit="dateRange" placeholder="Select Publish Date" v-model="published_on"></datepicker>
            </div>
        </div>
        <div class="form-group">
            <label>Web Image Upload (Small)</label> <small><i>Max Size 5MB</i></small><br/>
            <input type="file" ref="imageFile" class="form-control" @change="onAttachment" accept="image/jpeg, image/png">
        </div>
        <div class="form-group">
            <label>Social Image Upload (Large)</label> <small><i>Max Size 5MB</i></small><br/>
            <input type="file" ref="imageFileLarge" class="form-control" @change="onAttachmentSocial" accept="image/jpeg, image/png">
        </div>
        <template v-slot:footer>
            <div class="d-flex justify-content-end">
                <button @click="modal = !modal" class="btn btn-default mx-2">Cancel</button>
                <button @click="save" class="btn btn-primary">Save</button>
            </div>
        </template>
    </modal>
</template>

<script lang="ts">
    import {defineComponent, reactive, ref} from "vue";
    import Datepicker from 'vue3-datepicker';
    import Modal from "@/components/modal.vue";
    import moment from 'moment';
    import {IArticle} from '@/interfaces/article';
    import {store} from "@/store";
    import {MutationTypes} from "@/store/modules/ux/mutations";
    import {NotificationIcon,NotificationColor} from "@/interfaces/notification";
    import {Article} from "@/models/article";

    export default defineComponent({
        name: "CreateArticle",
        components: {
            Datepicker,
            Modal
        },
        props:{
            authorsObj:{type:Object, required:true},
        },
        emits:['update'],
        computed:{
            dateRange(){
                let today = new Date();
                let priorDate = new Date(new Date().setDate(today.getDate() - 30));
                return priorDate;
            }
        },
        setup(props, {emit}){
            let file = ref<File>();
            let fileLarge = ref<File>();
            const modal = ref(false);
            const open = ()=>modal.value = !modal.value;
            const title = ref('');
            const description = ref('');
            const article_text = ref('');
            const author_id = ref('');
            const published_on = ref(null);
            const selected_author = ref('');
            const validation = reactive({
                'title':true,
                'description':true,
                'article_text':true,
                'published_on':true,
            })
            const onAttachment = async(event:any) => {
                file.value = event.target.files[0];
            }
            const onAttachmentSocial = async(event:any) => {
                fileLarge.value = event.target.files[0];
            }
            const resetForm = ()=>{
                title.value = '';
                description.value = '';
                article_text.value = '';
                selected_author.value = '';
                published_on.value = null;
            }
            const resetValidation = ()=>{
                validation.title = true;
                validation.description = true;
                validation.article_text = true;
                validation.published_on = true;
            }

            const save = () =>{
                //reset validation
                resetValidation();

                //check validation
                if(title.value == '') validation.title = false;
                if(description.value == '') validation.description = false;
                if(article_text.value == '') validation.article_text = false;
                if(published_on.value == null) validation.published_on = false;

                if(validation.title && validation.description && validation.article_text && validation.published_on){
                    //save
                    let formData = new FormData();
                    formData.append("title", title.value);
                    formData.append("description", description.value);
                    formData.append("article_text", article_text.value);
                    formData.append("author_id", selected_author.value);
                    formData.append("published_on", moment(published_on.value).format('YYYY-MM-DD'));
                    formData.append("file", file.value!);
                    formData.append("file_large", fileLarge.value!);

                    Article.saveArticle(formData).then((res)=>{
                        let article:IArticle = res.data;
                        emit('update',article);
                        modal.value = false;
                        resetForm();
                        store.commit(MutationTypes.addSystemNotification,{title:'Success',content:'The article has been created.',icon:NotificationIcon.SUCCESS,color:NotificationColor.GREEN});
                    });
                }
            }

            return{
                Article,
                article_text,
                author_id,
                description,
                modal,
                moment,
                onAttachment,
                onAttachmentSocial,
                open,
                published_on,
                save,
                selected_author,
                title,
                validation,
            }
        }
    });
</script>

<style scoped>

</style>
